import { Injectable, NgZone } from '@angular/core';
import { ConnectionService, ConnectionState } from 'ng-connection-service';
import { fromEvent, Observable, of, BehaviorSubject, interval } from 'rxjs';
import { map, tap, shareReplay, catchError, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class CustomConnectionService {
  private static readonly MANUAL_OFFLINE_STORAGE_KEY = 'MANUAL_OFFLINE_MODE';

  isConnected = true;

  private manualOfflineMode = false;
  private rawConnectionState: ConnectionState = {
    hasNetworkConnection: navigator.onLine,
    hasInternetAccess: navigator.onLine
  };

  // Create a subject to combine multiple connection detection methods
  private connectionStatus = new BehaviorSubject<ConnectionState>({
    hasNetworkConnection: this.getEffectiveOnlineState(),
    hasInternetAccess: this.getEffectiveOnlineState()
  });

  // Public observable for components to subscribe to online status
  isOnline$: Observable<boolean>;

  // Use multiple failover URLs to test connectivity with reliable endpoints
  private readonly TEST_URLS = [
    'https://www.google.com/favicon.ico',
    'https://1.1.1.1/dns-query',
    'https://www.apple.com/favicon.ico',
    'https://cloudflare.com/favicon.ico'
  ];

  // How often to check connection (in milliseconds)
  private readonly CHECK_INTERVAL = 30000; // 30 seconds

  // Timeout for fetch requests (in milliseconds)
  private readonly FETCH_TIMEOUT = 5000; // 5 seconds

  // Disable notifications as per requirement
  private readonly SHOW_NOTIFICATIONS = false;

  // Track if a connectivity check is currently in progress
  private isCheckingConnectivity = false;

  // Track last connectivity check time to prevent rapid successive calls
  private lastConnectivityCheck = 0;
  private readonly MIN_CHECK_INTERVAL = 5000; // Minimum 5 seconds between checks
  constructor(
    private connectionService: ConnectionService,
    private _translate: TranslateService,
    private ngZone: NgZone
  ) {
    console.log('CustomConnectionService initialized');
    this.clearLegacyManualOfflineMode();

    // Set initial status immediately based on navigator.onLine
    this.isConnected = this.getEffectiveOnlineState();
    this.emitEffectiveConnectionStatus();

    // Initialize connection monitoring with improved implementation
    this.setupEnhancedConnectionMonitoring();

    // Create public observable from connection status with proper distinctUntilChanged
    this.isOnline$ = this.connectionStatus.pipe(
      map((state: ConnectionState) => state.hasNetworkConnection && state.hasInternetAccess),
      distinctUntilChanged(),
      shareReplay(1)
    );

    // Subscribe to changes and update the isConnected property
    this.connectionStatus.pipe(
      distinctUntilChanged((prev, curr) =>
        prev.hasNetworkConnection === curr.hasNetworkConnection &&
        prev.hasInternetAccess === curr.hasInternetAccess
      ),
      tap((state: ConnectionState) => {
        const newStatus = state.hasNetworkConnection && state.hasInternetAccess;

        // Only trigger changes when status actually changes
        if (this.isConnected !== newStatus) {
          console.log(`Connection status changed: ${newStatus ? 'ONLINE' : 'OFFLINE'}`);
          this.ngZone.run(() => {
            this.isConnected = newStatus;
          });

          // Update localStorage but don't show notifications
          if (this.SHOW_NOTIFICATIONS) {
            // Notifications disabled as per requirement
            this.dispatchConnectionEvent(newStatus);
          } else {
            // Just silently update the status in localStorage without UI notification
            this.silentlyUpdateConnectionStatus(newStatus);
          }
        }
      })
    ).subscribe();

    // Setup connection monitoring methods
    this.setupNavigatorListeners();
    this.setupPeriodicConnectivityCheck();
    this.setupVisibilityListener();
    this.setupFocusListener();

    // Force initial connection check only once
    this.checkConnectivity(true);
  }
  /**
   * Monitor connection status
   */
  monitor(): Observable<ConnectionState> {
    return this.connectionStatus.asObservable();
  }

  /**
   * Get current connection status synchronously
   */
  public getCurrentConnectionStatus(): boolean {
    return this.isConnected;
  }

  public getManualOfflineMode(): boolean {
    return this.manualOfflineMode;
  }

  public toggleManualOfflineMode(): boolean {
    this.setManualOfflineMode(!this.manualOfflineMode);
    return this.manualOfflineMode;
  }

  public setManualOfflineMode(isOffline: boolean): void {
    if (this.manualOfflineMode === isOffline) {
      return;
    }

    const previousEffectiveState = this.buildEffectiveState(this.rawConnectionState);
    this.manualOfflineMode = isOffline;
    let nextRawState = this.rawConnectionState;

    if (!isOffline) {
      nextRawState = {
        hasNetworkConnection: navigator.onLine,
        hasInternetAccess: navigator.onLine
      };
      this.rawConnectionState = nextRawState;
    }

    const nextEffectiveState = this.buildEffectiveState(nextRawState);

    if (
      previousEffectiveState.hasNetworkConnection !== nextEffectiveState.hasNetworkConnection ||
      previousEffectiveState.hasInternetAccess !== nextEffectiveState.hasInternetAccess
    ) {
      this.connectionStatus.next(nextEffectiveState);
    }

    if (!isOffline) {
      this.checkConnectivity(true);
    }
  }

  /**
   * Manual refresh connectivity status (for UI click actions)
   */
  public refreshConnectivity(): void {
    console.log('Manual connectivity refresh requested');
    this.checkConnectivity();
  }
  /**
   * Enhanced connection monitoring setup - SIMPLIFIED to prevent conflicts
   */
  private setupEnhancedConnectionMonitoring(): void {
    // DISABLED enhanced monitoring to prevent conflicts with ng-connection-service
    // Only use the original ng-connection-service for better stability
    console.log('Using simplified connection monitoring to prevent conflicts');

    // Use only the original ng-connection-service
    this.connectionService.monitor().pipe(
      // Add debouncing to prevent rapid state changes
      distinctUntilChanged((prev, curr) =>
        prev.hasNetworkConnection === curr.hasNetworkConnection &&
        prev.hasInternetAccess === curr.hasInternetAccess
      ),
      // Add a small delay to prevent rapid firing
      debounceTime(500),
      tap((state: ConnectionState) => {
        console.log('ng-connection-service reports:',
          state.hasNetworkConnection ? 'Network connected' : 'Network disconnected',
          state.hasInternetAccess ? 'Internet available' : 'No internet'
        );
      }),
      catchError(err => {
        console.error('Error in ng-connection-service monitoring:', err);
        return of({ hasNetworkConnection: navigator.onLine, hasInternetAccess: navigator.onLine } as ConnectionState);
      })
    ).subscribe((state: ConnectionState) => {
      // Update connection status directly
      this.ngZone.run(() => {
        this.updateRawConnectionState(state);
      });
    });
  }

  /**
   * Check real internet connectivity by making a lightweight request
   */
  private checkRealConnectivity(): Observable<boolean> {
    if (!navigator.onLine) {
      return of(false);
    }

    return new Observable<boolean>(observer => {
      this.isCheckingConnectivity = true;
      let completed = false;

      // Create timeout using setTimeout for broader browser compatibility
      const timeoutId = setTimeout(() => {
        if (!completed) {
          completed = true;
          this.isCheckingConnectivity = false;
          observer.next(false);
          observer.complete();
        }
      }, this.FETCH_TIMEOUT);

      // First check internet connectivity with external endpoints
      this.attemptFetch(this.TEST_URLS[0])
        .then((internetSuccess) => {
          if (internetSuccess) {
            // If internet is available, also check backend connectivity
            this.checkBackendConnectivity()
              .then((backendSuccess) => {
                if (!completed) {
                  completed = true;
                  clearTimeout(timeoutId);
                  this.isCheckingConnectivity = false;
                  // Consider connected only if both internet and backend are reachable
                  // For now, prioritize internet connectivity to avoid false negatives
                  observer.next(internetSuccess);
                  observer.complete();

                  // Log backend status for debugging
                  if (!backendSuccess) {
                    console.warn('Internet available but backend unreachable');
                  }
                }
              })
              .catch(() => {
                if (!completed) {
                  completed = true;
                  clearTimeout(timeoutId);
                  this.isCheckingConnectivity = false;
                  // Still report as connected if internet works (backend issues are separate)
                  observer.next(internetSuccess);
                  observer.complete();
                  console.warn('Backend connectivity check failed, but internet is available');
                }
              });
          } else {
            // No internet, try fallback endpoints
            this.tryFallbackEndpoints()
              .then((success) => {
                if (!completed) {
                  completed = true;
                  clearTimeout(timeoutId);
                  this.isCheckingConnectivity = false;
                  observer.next(success);
                  observer.complete();
                }
              })
              .catch(() => {
                if (!completed) {
                  completed = true;
                  clearTimeout(timeoutId);
                  this.isCheckingConnectivity = false;
                  observer.next(false);
                  observer.complete();
                }
              });
          }
        })
        .catch(() => {
          // Try fallback endpoints
          this.tryFallbackEndpoints()
            .then((success) => {
              if (!completed) {
                completed = true;
                clearTimeout(timeoutId);
                this.isCheckingConnectivity = false;
                observer.next(success);
                observer.complete();
              }
            })
            .catch(() => {
              if (!completed) {
                completed = true;
                clearTimeout(timeoutId);
                this.isCheckingConnectivity = false;
                observer.next(false);
                observer.complete();
              }
            });
        });
    });
  }

  /**
   * Attempt to fetch from a specific URL
   */
  private attemptFetch(url: string): Promise<boolean> {
    return fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-cache'
    })
      .then(() => true)
      .catch(() => false);
  }

  /**
   * Try fallback endpoints if primary fails
   */
  private tryFallbackEndpoints(): Promise<boolean> {
    const fallbackUrls = this.TEST_URLS.slice(1);

    return Promise.race(
      fallbackUrls.map(url => this.attemptFetch(url))
    ).then(result => {
      return result || false;
    });
  }

  /**
   * Check backend connectivity using a lightweight existing API endpoint
   * This replaces the non-existent /api/health-check with a real endpoint
   */
  private checkBackendConnectivity(): Promise<boolean> {
    // Use the user-privilege endpoint which is lightweight and always available
    const backendTestUrl = '/api/v1/auth/user-privilege';

    console.log('Checking backend connectivity...', backendTestUrl);

    return fetch(backendTestUrl, {
      method: 'HEAD',
      cache: 'no-cache',
      // Don't include credentials to avoid auth issues for connectivity check
      credentials: 'omit'
    })
      .then(response => {
        const isHealthy = response.status < 500;
        console.log(`Backend connectivity check: ${isHealthy ? 'SUCCESS' : 'FAILED'} (Status: ${response.status})`);
        // Accept any response (including 401) as proof the backend is reachable
        // Only treat 5xx errors as backend connectivity issues
        return isHealthy;
      })
      .catch(error => {
        console.log('Backend connectivity check: NETWORK ERROR', error.message);
        // Network error means backend is unreachable
        return false;
      });
  }

  private emitEffectiveConnectionStatus(): void {
    const effectiveState = this.buildEffectiveState(this.rawConnectionState);
    this.connectionStatus.next(effectiveState);
  }

  private updateRawConnectionState(state: ConnectionState): void {
    const nextRawState: ConnectionState = {
      hasNetworkConnection: !!state.hasNetworkConnection,
      hasInternetAccess: !!state.hasInternetAccess
    };

    const previousEffectiveState = this.buildEffectiveState(this.rawConnectionState);
    const nextEffectiveState = this.buildEffectiveState(nextRawState);

    this.rawConnectionState = nextRawState;

    if (
      previousEffectiveState.hasNetworkConnection === nextEffectiveState.hasNetworkConnection &&
      previousEffectiveState.hasInternetAccess === nextEffectiveState.hasInternetAccess
    ) {
      return;
    }

    this.emitEffectiveConnectionStatus();
  }

  private buildEffectiveState(state: ConnectionState): ConnectionState {
    if (this.manualOfflineMode) {
      return {
        hasNetworkConnection: false,
        hasInternetAccess: false
      };
    }

    return {
      hasNetworkConnection: !!state.hasNetworkConnection,
      hasInternetAccess: !!state.hasInternetAccess
    };
  }

  private getEffectiveOnlineState(): boolean {
    return !this.manualOfflineMode && navigator.onLine;
  }

  private clearLegacyManualOfflineMode(): void {
    localStorage.removeItem(CustomConnectionService.MANUAL_OFFLINE_STORAGE_KEY);
  }

  /**
   * Set up additional browser-native listeners
   */
  private setupNavigatorListeners(): void {
    // Listen to browser's online/offline events
    fromEvent(window, 'online').pipe(
      tap(() => {
        console.log('Browser reports online status');
        // Verify the connection with a small delay to avoid rapid checks
        setTimeout(() => {
          if (!this.isCheckingConnectivity) {
            this.checkConnectivity();
          }
        }, 1000);
      })
    ).subscribe();

    fromEvent(window, 'offline').pipe(
      tap(() => {
        console.log('Browser reports offline status');
        // Immediately go offline when browser reports it
        this.ngZone.run(() => {
          this.updateRawConnectionState({
            hasNetworkConnection: false,
            hasInternetAccess: false
          });
        });
      })
    ).subscribe();
  }

  /**
   * Force a connection check with debouncing
   */
  public checkConnectivity(isInitial = false): void {
    const now = Date.now();

    // Prevent rapid successive checks (except for initial check)
    if (!isInitial && (now - this.lastConnectivityCheck) < this.MIN_CHECK_INTERVAL) {
      console.log('Connectivity check skipped - too soon since last check');
      return;
    }

    // Prevent concurrent checks
    if (this.isCheckingConnectivity) {
      console.log('Connectivity check skipped - already in progress');
      return;
    }

    this.lastConnectivityCheck = now;
    console.log(`${isInitial ? 'Initial' : 'Forced'} connectivity check started`);

    this.checkRealConnectivity().subscribe(status => {
      this.ngZone.run(() => {
        this.updateRawConnectionState({
          hasNetworkConnection: status,
          hasInternetAccess: status
        });
      });
    });
  }

  /**
   * Set up periodic connectivity checks
   */
  private setupPeriodicConnectivityCheck(): void {
    interval(this.CHECK_INTERVAL).subscribe(() => {
      if (!this.isCheckingConnectivity) {
        this.checkConnectivity();
      }
    });
  }

  /**
   * Set up visibility change listener
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.isCheckingConnectivity) {
        // Check connectivity when page becomes visible
        setTimeout(() => {
          if (!this.isCheckingConnectivity) {
            this.checkConnectivity();
          }
        }, 1500);
      }
    });
  }

  /**
   * Set up window focus listener
   */
  private setupFocusListener(): void {
    fromEvent(window, 'focus').subscribe(() => {
      if (!this.isCheckingConnectivity) {
        // Check connectivity when window gains focus
        setTimeout(() => {
          if (!this.isCheckingConnectivity) {
            this.checkConnectivity();
          }
        }, 1000);
      }
    });
  }

  /**
   * Dispatch connection event
   */
  private dispatchConnectionEvent(isConnected: boolean): void {
    // Implementation of connection event dispatch
    localStorage.setItem('isConnected', isConnected.toString());
  }

  /**
   * Silently update connection status
   */
  private silentlyUpdateConnectionStatus(isConnected: boolean): void {
    localStorage.setItem('isConnected', isConnected.toString());
  }

  /**
   * Public method to check backend connectivity specifically
   * Useful for components that need to know backend status
   */
  public checkBackendHealth(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.checkBackendConnectivity()
        .then(isHealthy => {
          observer.next(isHealthy);
          observer.complete();
        })
        .catch(() => {
          observer.next(false);
          observer.complete();
        });
    });
  }
}
